/**
 * JoinInvitation.jsx — public accept-invitation page (no auth, no Layout).
 *
 * Reads `?token=` from the URL, shows who invited them, and lets the invitee set
 * a name + password to create their employee account. All tenant data comes
 * from the server (by token) — this page never sends tenant/permission data,
 * only name + password.
 *
 * The password field mirrors the normal Register page: a live strength meter,
 * a hint, and a show/hide toggle. The backend enforces the same min-8 rule.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Eye, EyeOff, Info } from 'lucide-react';
import toast from 'react-hot-toast';

import { useLanguage } from '@/components/LanguageContext';
import { getInvitationInfo, acceptInvitation } from '@/api/collaboratorsService';
import { gbusinessErrorMessage } from '@/api/gbusinessErrors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { countries } from '@/lib/phoneCountries';
import tapstarLogo from '@/img/tapstar_logo.png';

/** Same 0–100 strength heuristic as the Register page. */
function passwordStrength(pass) {
  let s = 0;
  if (pass.length >= 8) s += 25;
  if (/[A-Z]/.test(pass)) s += 25;
  if (/[0-9]/.test(pass)) s += 25;
  if (/[^A-Za-z0-9]/.test(pass)) s += 25;
  return s;
}

function strengthColor(strength) {
  if (strength <= 25) return 'bg-red-500';
  if (strength <= 50) return 'bg-orange-500';
  if (strength <= 75) return 'bg-yellow-500';
  return 'bg-green-500';
}

export default function JoinInvitation() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState('loading'); // loading | ok | invalid
  const [info, setInfo] = useState(null);
  const [name, setName] = useState('');
  const [countryPrefix, setCountryPrefix] = useState('+34');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const strength = passwordStrength(password);

  useEffect(() => {
    let active = true;
    if (!token) { setStatus('invalid'); return; }
    getInvitationInfo(token)
      .then((data) => { if (active) { setInfo(data); setStatus('ok'); } })
      .catch(() => { if (active) setStatus('invalid'); });
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t('team.passwordTooShort') || 'La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      toast.error(t('team.passwordsMismatch') || 'Las contraseñas no coinciden.');
      return;
    }
    // Phone required; basic client-side sanity — the backend validates it for
    // the prefix with libphonenumber (same as the normal register).
    const nationalNumber = phone.replace(/[\s()\-.]/g, '');
    if (!/^\d{4,15}$/.test(nationalNumber)) {
      toast.error(t('invalid_phone') || 'Introduce un número de teléfono válido.');
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvitation({
        token,
        name: name.trim(),
        password,
        phone: `${countryPrefix}${nationalNumber}`,
        phonePrefix: countryPrefix,
      });
      toast.success(t('team.accountCreated') || 'Cuenta creada. Ya puedes iniciar sesión.');
      navigate('/login');
    } catch (err) {
      toast.error(gbusinessErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
        <div className="flex justify-center mb-6">
          <img src={tapstarLogo} alt="Tapstar" className="h-10 w-auto" />
        </div>

        {status === 'loading' && (
          <p className="text-center text-slate-500">{t('loading') || 'Cargando…'}</p>
        )}

        {status === 'invalid' && (
          <div className="text-center space-y-3">
            <XCircle className="w-10 h-10 text-red-500 mx-auto" />
            <p className="font-semibold text-slate-900">
              {t('team.inviteInvalidTitle') || 'Invitación no válida'}
            </p>
            <p className="text-sm text-slate-500">
              {t('team.inviteInvalidBody') || 'Este enlace no es válido o ha expirado. Pide una nueva invitación.'}
            </p>
            <Link to="/login" className="text-sm text-[#e69a1a] font-medium hover:underline inline-block mt-2">
              {t('login_now') || 'Ir a iniciar sesión'}
            </Link>
          </div>
        )}

        {status === 'ok' && info && (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-center space-y-1 mb-2">
              <CheckCircle2 className="w-9 h-9 text-emerald-500 mx-auto" />
              <p className="font-semibold text-slate-900">
                {info.tenantOwnerName
                  ? (t('team.invitedByHeading', { owner: info.tenantOwnerName }) ||
                     `${info.tenantOwnerName} te ha invitado a su equipo`)
                  : (t('team.invitedHeading') || 'Te han invitado a un equipo')}
              </p>
              <p className="text-sm text-slate-500">{info.email}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="join-name">{t('team.yourName') || 'Tu nombre'}</Label>
              <Input id="join-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t('team.yourNamePlaceholder') || 'Nombre y apellidos'} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="join-phone">{t('phone') || 'Teléfono'}</Label>
              <div className="flex gap-2">
                <select
                  value={countryPrefix}
                  onChange={(e) => setCountryPrefix(e.target.value)}
                  className="w-28 h-10 px-2.5 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#feae2c] cursor-pointer"
                  aria-label={t('phone') || 'Prefijo'}
                >
                  {countries.map((co) => (
                    <option key={co.code} value={co.prefix}>{co.flag} {co.prefix}</option>
                  ))}
                </select>
                <Input
                  id="join-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('phone') || 'Teléfono'}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="join-pass">{t('team.password') || 'Contraseña'}</Label>
              <div className="relative">
                <Input id="join-pass" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                  className="pr-11" />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                  aria-label={showPassword ? (t('hide_password') || 'Ocultar contraseña') : (t('show_password') || 'Mostrar contraseña')}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${strengthColor(strength)}`}
                    style={{ width: `${strength}%` }} />
                </div>
              )}
              <p className="text-[11px] text-slate-500 flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                {t('password_hint') || 'Usa al menos 8 caracteres, con mayúsculas, números y símbolos.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="join-confirm">{t('team.confirmPassword') || 'Confirmar contraseña'}</Label>
              <Input id="join-confirm" type={showPassword ? 'text' : 'password'} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </div>

            <Button type="submit" className="btn-primary w-full h-11" disabled={submitting}>
              {submitting ? (t('loading') || 'Creando…') : (t('team.acceptAndCreate') || 'Aceptar y crear cuenta')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
